import { Injectable, isDevMode } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';

interface IdentifiableUser {
  username?: string;
  email?: string;
  name?: string;
  location_token?: string;
  location_type?: string;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private enabled = false;
  private identifyDone = false;
  private identifiedUsername: string | null = null;
  private identifiedAccount: string | null = null;
  private loggedFailureOnce = false;
  // Angular's zone.js typically routes the same uncaught error/unhandled
  // rejection to BOTH the native window event (installGlobalErrorCapture)
  // AND Angular's ErrorHandler (PosthogErrorHandler) since most app code
  // runs inside the Angular zone. Without dedup, one real error would be
  // reported to PostHog twice. Keyed by a cheap error signature, pruned
  // opportunistically since error volume is low.
  private recentErrorSignatures = new Map<string, number>();
  private static readonly ERROR_DEDUP_WINDOW_MS = 2000;

  init(): void {
    const key = environment.posthogKey;
    if (!key) {
      this.enabled = false;
      return;
    }
    try {
      posthog.init(key, {
        api_host: 'https://eu.i.posthog.com',
        autocapture: false,
        capture_pageview: false,
        capture_performance: false,
        disable_session_recording: false,
        mask_all_text: true,
        mask_all_element_attributes: true,
        persistence: 'localStorage+cookie',
      });
      this.enabled = true;
      this.installGlobalErrorCapture();
    } catch (err) {
      this.handleFailure(err);
    }
  }

  /** Reports uncaught errors and unhandled promise rejections that never pass
   * through Angular's ErrorHandler — e.g. errors thrown by code running
   * outside the Angular zone. Errors Angular does catch (component/template/
   * lifecycle errors) are reported via PosthogErrorHandler -> captureError(). */
  private installGlobalErrorCapture(): void {
    // Defensive: no SSR entry point exists in this app today, but init() is
    // called from an APP_INITIALIZER, which would also run during any future
    // server render — where `window` doesn't exist.
    if (typeof window === 'undefined') return;
    window.addEventListener('error', (event: ErrorEvent) => {
      this.captureError(event.error ?? event.message);
    });
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      this.captureError(event.reason);
    });
  }

  identify(user: IdentifiableUser | null | undefined): void {
    if (!this.enabled || !user) return;
    // /verify returns { name, email, is_super_user } while /api/user-details returns
    // { username, email, location_token, location_type, name: <location name>, ... }
    // — same user.name column, two different aliases. Accept either as the distinct ID.
    const distinctId = user.username ?? user.name;
    if (!distinctId) return;
    try {
      // AuthGuard.verifyToken runs on every guarded route activation. Only call
      // posthog.identify on the first call for a given distinct ID; the group call
      // below still fires every time so a follow-up identify with the richer
      // /api/user-details payload can add the account group.
      if (this.identifiedUsername !== distinctId) {
        posthog.identify(distinctId, {
          is_internal: (user.email ?? '').toLowerCase().endsWith('@ontapdata.com.au'),
        });
        this.identifiedUsername = distinctId;
        if (!this.identifyDone) {
          this.track('auth:identified');
          this.identifyDone = true;
        }
      }
      if (user.location_token && this.identifiedAccount !== user.location_token) {
        // /api/user-details aliases users.name AS username and exposes l.name as
        // user.name (the customer/organisation, e.g. "Thirsty Camel"). When the
        // call comes from /verify there is no location_token so this block is skipped.
        // Gated on identifiedAccount so navigation-loop re-identifies don't refire.
        posthog.group('account', user.location_token, {
          name: user.name ?? user.location_token,
          type: user.location_type ?? '',
        });
        this.identifiedAccount = user.location_token;
      }
    } catch (err) {
      this.handleFailure(err);
    }
  }

  resetUser(): void {
    if (!this.enabled) return;
    try {
      posthog.reset();
      this.identifyDone = false;
      this.identifiedUsername = null;
      this.identifiedAccount = null;
    } catch (err) {
      this.handleFailure(err);
    }
  }

  track(eventName: string, properties: Record<string, unknown> = {}): void {
    if (!this.enabled) return;
    try {
      posthog.capture(eventName, {
        app_version: environment.version,
        environment: this.deriveEnvironmentLabel(),
        ...properties,
      });
    } catch (err) {
      this.handleFailure(err);
    }
  }

  trackPageView(routeTemplate: string): void {
    this.track('$pageview', {
      $current_url: routeTemplate,
      route_template: routeTemplate,
    });
  }

  /** Reports an error to PostHog's Error Tracking product. Safe to call even
   * when analytics is disabled (e.g. local dev with no posthogKey). Drops
   * near-duplicate reports of the same error arriving via both capture paths
   * (see recentErrorSignatures above). */
  captureError(error: unknown, properties: Record<string, unknown> = {}): void {
    if (!this.enabled) return;
    if (this.isDuplicateError(error)) return;
    try {
      posthog.captureException(error, {
        app_version: environment.version,
        environment: this.deriveEnvironmentLabel(),
        ...properties,
      });
    } catch (err) {
      this.handleFailure(err);
    }
  }

  private isDuplicateError(error: unknown): boolean {
    const now = Date.now();
    const cutoff = now - AnalyticsService.ERROR_DEDUP_WINDOW_MS;
    for (const [key, seenAt] of this.recentErrorSignatures) {
      if (seenAt < cutoff) this.recentErrorSignatures.delete(key);
    }
    const signature = this.errorSignature(error);
    if (this.recentErrorSignatures.has(signature)) return true;
    this.recentErrorSignatures.set(signature, now);
    return false;
  }

  private errorSignature(error: unknown): string {
    if (error instanceof Error) {
      // name + message + first stack frame is a cheap, stable-enough
      // fingerprint without needing a hashing library.
      const firstFrame = (error.stack ?? '').split('\n')[1] ?? '';
      return `${error.name}:${error.message}:${firstFrame}`;
    }
    return String(error);
  }

  private deriveEnvironmentLabel(): 'production' | 'staging' | 'development' | 'local' {
    if (environment.isLocal) return 'local';
    const v = environment.version ?? '';
    if (v.endsWith('-staging')) return 'staging';
    if (v.endsWith('-dev')) return 'development';
    return 'production';
  }

  private handleFailure(err: unknown): void {
    if (isDevMode() && !this.loggedFailureOnce) {
      console.warn('[AnalyticsService] PostHog call failed, suppressing further warnings:', err);
      this.loggedFailureOnce = true;
    }
  }
}
